import type { Maybe } from '@adornis/base/utilTypes.js';
import type { PaymentStatus, SubscriptionStatus } from '@adornis/payments/db/payments.js';

/**
 * Back in the day (2024), there was a short period of time where Bayern and RLP diverged in their way to grade and run the exam day.
 * This is now history because all states have chosen to follow the Bayern-model.
 */
export enum ExamDayRatingType {
  BAYERN = 'FAMED_BAYERN', // only here for backwards compat
  RLP = 'FAMED_RLP', // only here for backwards compat
  FAMED = 'FAMED',
}

export enum ExamType {
  FAMED = 'FAMED',
  FSPP = 'FSPP',
}

export enum embedPages {
  SIGNUP = 'signup',
  EXAMDAYS = 'examdays',
}

export enum FederalStatesGermany {
  BADEN_WUERTTEMBERG = 'Baden-Württemberg',
  BAYERN = 'Bayern',
  BERLIN = 'Berlin',
  BRANDENBURG = 'Brandenburg',
  BREMEN = 'Bremen',
  HAMBURG = 'Hamburg',
  HESSEN = 'Hessen',
  MECKLENBURG_VORPOMMERN = 'Mecklenburg-Vorpommern',
  NIEDERSACHSEN = 'Niedersachsen',
  NORDRHEIN_WESTFALEN = 'Nordrhein-Westfalen',
  RHEINLAND_PFALZ = 'Rheinland-Pfalz',
  SAARLAND = 'Saarland',
  SACHSEN = 'Sachsen',
  SACHSEN_ANHALT = 'Sachsen-Anhalt',
  SCHLESWIG_HOLSTEIN = 'Schleswig-Holstein',
  THUERINGEN = 'Thüringen',
}

export const RATING_CONFIG_NAMES = ['4choices', '2choices'] as const;
/**
 * 4choices is legacy. This was the old way to grade (before end of 2024). 2choices is the new way.
 */
export type RatingConfigName = (typeof RATING_CONFIG_NAMES)[number];

export type TaskType = 'voice' | 'text';
export const CONVERSATION_TYPES = ['DrPa', 'DrPf', 'DrDr', 'PfPa', 'PfPf'] as const;
export type ConversationType = (typeof CONVERSATION_TYPES)[number];

export const prettyConversationType = (conversationType: ConversationType): string =>
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  ({
    [CONVERSATION_TYPES[0]]: 'Arzt - Patient',
    [CONVERSATION_TYPES[1]]: 'Arzt - Pfleger',
    [CONVERSATION_TYPES[2]]: 'Arzt - Arzt',
    [CONVERSATION_TYPES[3]]: 'Pfleger - Patient',
    [CONVERSATION_TYPES[4]]: 'Pfleger - Pfleger',
  }[conversationType] ?? `Unbekannter Konversationstyp ${conversationType}`); // should never happen

export enum OSCECategory {
  FAMED_ANMANESEGESPRAECH = 'FAMED_ANMANESEGESPRAECH',
  FAMED_AUFKLAERUNGSGESPERAECH = 'FAMED_AUFKLAERUNGSGESPERAECH',
  FAMED_PATIENTEN_VORSTELLUNG = 'FAMED_PATIENTEN_VORSTELLUNG',
  FAMED_PATIENTEN_UEBERGABE = 'FAMED_PATIENTEN_UEBERGABE',
  FAMED_ARZTBRIEF_ANAMNESE = 'FAMED_ARZTBRIEF_ANAMNESE',
  FSPP_ANMANESEGESPRAECH = 'FSPP_ANMANESEGESPRAECH',
  FSPP_AUFKLAERUNGSGESPERAECH = 'FSPP_AUFKLAERUNGSGESPERAECH',
  FSPP_PATIENTEN_VORSTELLUNG = 'FSPP_PATIENTEN_VORSTELLUNG',
  FSPP_PATIENTEN_UEBERGABE = 'FSPP_PATIENTEN_UEBERGABE',
  FSPP_ARZTBRIEF_ANAMNESE = 'FSPP_ARZTBRIEF_ANAMNESE',
  // TODO add FSPP categories once they are decided
}
export const OsceCategories = Object.values(OSCECategory);

export const OsceCategoriesByExamType = {
  [ExamType.FAMED]: OsceCategories.filter(category => category.startsWith('FAMED_')),
  [ExamType.FSPP]: OsceCategories.filter(category => category.startsWith('FSPP_')),
};

export const TextOsceCategories = [OSCECategory.FAMED_ARZTBRIEF_ANAMNESE, OSCECategory.FSPP_ARZTBRIEF_ANAMNESE];
export const isTextOsceCategory = (osceCategory: Maybe<OSCECategory>): boolean =>
  !!osceCategory && TextOsceCategories.includes(osceCategory);

export const prettyOsceCategory = (osceCategory: OSCECategory): string =>
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  ({
    [OSCECategory.FAMED_ANMANESEGESPRAECH]: 'FAMED Anamnesegespräch',
    [OSCECategory.FAMED_AUFKLAERUNGSGESPERAECH]: 'FAMED Aufklärungsgespräch',
    [OSCECategory.FAMED_PATIENTEN_VORSTELLUNG]: 'FAMED Patientenvorstellung',
    [OSCECategory.FAMED_PATIENTEN_UEBERGABE]: 'FAMED Patientenübergabe',
    [OSCECategory.FAMED_ARZTBRIEF_ANAMNESE]: 'FAMED Arztbrief: Anamnese',
    [OSCECategory.FSPP_ANMANESEGESPRAECH]: 'FSPP Anamnesegespräch',
    [OSCECategory.FSPP_AUFKLAERUNGSGESPERAECH]: 'FSPP Aufklärungsgespräch',
    [OSCECategory.FSPP_PATIENTEN_VORSTELLUNG]: 'FSPP Patientenvorstellung',
    [OSCECategory.FSPP_PATIENTEN_UEBERGABE]: 'FSPP Patientenübergabe',
    [OSCECategory.FSPP_ARZTBRIEF_ANAMNESE]: 'FSPP Arztbrief: Anamnese',
  }[osceCategory] ?? `Unbekannte OSCE-Kategorie ${osceCategory}`); // should never happen

export function mapExamDayTypeToFederalState(examDayType: ExamDayRatingType) {
  switch (examDayType) {
    case ExamDayRatingType.BAYERN:
      return FederalStatesGermany.BAYERN;
    case ExamDayRatingType.RLP:
      return FederalStatesGermany.RHEINLAND_PFALZ;
    default:
      return null;
  }
}

export enum UserRole {
  ADMIN = 'admin',
  TRAINEE = 'trainee', // permission -> candidate
  TESTER = 'tester', // permission -> actor
  INSPECTOR = 'inspector',
  EXAMCENTER = 'examCenter',
  EXAMCENTERMANAGER = 'examCenterManager',
  DIGIFORT = 'digifort',
  SUPPORT = 'support',
  ACCOUNTING = 'accounting',
  RESOLVE_ERRORS = 'resolveErrors',
}

export function prettyUserRole(role: UserRole) {
  return (
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    {
      [UserRole.ADMIN]: 'Admins',
      [UserRole.TRAINEE]: 'Prüfling',
      [UserRole.TESTER]: 'Schauspieler',
      [UserRole.INSPECTOR]: 'Bewerter',
      [UserRole.EXAMCENTER]: 'Prüfungszentren',
      [UserRole.EXAMCENTERMANAGER]: 'Prüfungsleiter',
      [UserRole.DIGIFORT]: 'Digifort',
      [UserRole.SUPPORT]: 'Support',
      [UserRole.ACCOUNTING]: 'Accounting',
      [UserRole.RESOLVE_ERRORS]: 'Fehlerbehebung',
    }[role] ?? `Unbekannter Rollenname ${role}`
  );
}

export enum Gender {
  MALE = 'Männlich',
  FEMALE = 'Weiblich',
  DIVERS = 'Divers',
}

export enum Specialization {
  DOCTOR = 'Mediziner',
  LINGUIST = 'Sprachwissenschaftler',
}

export const DATA_VIEW_CONFIG_KEYS = ['user-export-line', 'user-basic-data', 'rating-export'] as const;
export type DataViewConfigKey = (typeof DATA_VIEW_CONFIG_KEYS)[number];

export type OrderStatus = PaymentStatus | 'revoked' | 'moved' | SubscriptionStatus;

export const prettyOrderStatus = (status: OrderStatus): string => {
  return (
    {
      issued: 'Bestellung aufgegeben',
      canceled: 'Abgebrochen',
      revoked: 'Storniert',
      moved: 'Verschoben',
      paid: 'Bezahlt',
      failed: 'Fehlgeschlagen',
      expired: 'Abgelaufen',
      refund: 'Storniert',
    }[status] ?? status
  );
};
